// Pure-function flood fill. Operates on a raw RGBA Uint8ClampedArray; never
// touches DOM or Canvas. The engine wrapper in engine.ts handles canvas I/O.
//
// Invariant assumed: lineart has already been binarized by process-assets.mjs.
// Pure black lines (each channel < 60), pure white regions, zero mid-gray.
// That lets us:
//   - reject "click on a line" with a fast per-channel check
//   - skip any anti-alias / edge-blend post-pass — there are no transitional
//     pixels to clean up
//   - run a tight tolerance (50, strict <) without losing legit fills
//
// Algorithm: scanline flood fill with a stack — fewer push/pop than the
// 4-direction recursive variant. While filling, records each pixel's
// Euclidean distance from the click point so the React layer can drive a
// ripple-reveal animation in fill order rather than a sudden blit.

import type { FillResult, RGB } from './types.ts'

export interface FloodFillArgs {
  /** RGBA, length 4 * width * height. Mutated in place. */
  data: Uint8ClampedArray
  width: number
  height: number
  startX: number
  startY: number
  replacement: RGB
  /** RGB Euclidean distance threshold for "same region" (strict <). Default 50. */
  threshold: number
}

/** Per-channel "this pixel is a line" cutoff. Matches the prototype. */
const LINE_PER_CHANNEL = 60
/** Per-channel tolerance for "click landed on a region already this color". */
const SAME_COLOR_PER_CHANNEL = 5

export function floodFill(args: FloodFillArgs): FillResult {
  const { data, width, height, startX, startY, replacement, threshold } = args

  if (startX < 0 || startX >= width || startY < 0 || startY >= height) {
    return { status: 'no-image' }
  }

  const i0 = (startY * width + startX) * 4
  const tr = data[i0]!
  const tg = data[i0 + 1]!
  const tb = data[i0 + 2]!

  // Click on a line pixel → reject. Per-channel: any of R/G/B ≥ 60 means it's
  // not a binarized black pixel. Matches prototype's startR<60 && startG<60 && startB<60.
  if (tr < LINE_PER_CHANNEL && tg < LINE_PER_CHANNEL && tb < LINE_PER_CHANNEL) {
    return { status: 'on-line' }
  }

  // Click on a region already this color → no-op. Per-channel abs < 5.
  if (
    Math.abs(tr - replacement.r) < SAME_COLOR_PER_CHANNEL &&
    Math.abs(tg - replacement.g) < SAME_COLOR_PER_CHANNEL &&
    Math.abs(tb - replacement.b) < SAME_COLOR_PER_CHANNEL
  ) {
    return { status: 'same-color' }
  }

  const target = { r: tr, g: tg, b: tb }
  const tSq = threshold * threshold

  const visited = new Uint8Array(width * height)
  const distanceMap = new Float32Array(width * height)
  distanceMap.fill(NaN)

  const matches = (x: number, y: number): boolean => {
    if (x < 0 || x >= width || y < 0 || y >= height) return false
    if (visited[y * width + x]) return false
    const i = (y * width + x) * 4
    const dr = data[i]! - target.r
    const dg = data[i + 1]! - target.g
    const db = data[i + 2]! - target.b
    return dr * dr + dg * dg + db * db < tSq
  }

  const stack: number[] = []
  stack.push(startY * width + startX)

  let filledCount = 0
  let minX = startX
  let minY = startY
  let maxX = startX
  let maxY = startY
  let maxDistance = 0

  while (stack.length > 0) {
    const seed = stack.pop()!
    const sx = seed % width
    const sy = Math.floor(seed / width)
    if (!matches(sx, sy)) continue

    let lx = sx
    while (lx > 0 && matches(lx - 1, sy)) lx--

    let x = lx
    let spanAbove = false
    let spanBelow = false
    while (x < width && matches(x, sy)) {
      const i = (sy * width + x) * 4
      data[i] = replacement.r
      data[i + 1] = replacement.g
      data[i + 2] = replacement.b
      data[i + 3] = 255
      visited[sy * width + x] = 1
      const dx = x - startX
      const dy = sy - startY
      const d = Math.sqrt(dx * dx + dy * dy)
      distanceMap[sy * width + x] = d
      if (d > maxDistance) maxDistance = d
      filledCount++
      if (x < minX) minX = x
      if (x > maxX) maxX = x

      if (sy > 0) {
        const aboveMatches = matches(x, sy - 1)
        if (aboveMatches && !spanAbove) {
          stack.push((sy - 1) * width + x)
          spanAbove = true
        } else if (!aboveMatches) {
          spanAbove = false
        }
      }
      if (sy < height - 1) {
        const belowMatches = matches(x, sy + 1)
        if (belowMatches && !spanBelow) {
          stack.push((sy + 1) * width + x)
          spanBelow = true
        } else if (!belowMatches) {
          spanBelow = false
        }
      }
      x++
    }
    if (sy < minY) minY = sy
    if (sy > maxY) maxY = sy
  }

  return {
    status: 'filled',
    filledPixelCount: filledCount,
    bounds: { minX, minY, maxX, maxY },
    distanceMap,
    maxDistance,
  }
}
